export const RARITIES = ['N', 'R', 'SR', 'SSR', 'UR', 'EXR'] as const;
export type Rarity = (typeof RARITIES)[number];
export type CosmeticKind = 'board-frame' | 'board-background';
export type Cosmetic = Readonly<{
  id: string; name: string; rarity: Rarity; kind: CosmeticKind;
  image: string; thumbnail: string; initial: boolean;
  projection?: Readonly<{ character: string; veil: number }>;
}>;
export const CATALOG_VERSION = 2;
// Art revisions keep the existing item IDs and acquisition history intact.
export const COSMETIC_ART_VERSION = '20260913-2';
export const RARITY_COLORS: Record<Rarity, string> = {
  N: '#bec8c1', R: '#6dc6f4', SR: '#b89cf5', SSR: '#eec971', UR: '#b8fff3', EXR: '#fff0c7',
};
const names: Record<Rarity, readonly [readonly string[], readonly string[]]> = {
  N: [['古木の縁', '苔石の縁', '黒鉄の縁', '砂岩の縁'], ['深緑のフェルト', '雨の石畳', '静かな土庭', '古紙の盤']],
  R: [['翡翠の縁', '月銀の縁', '紅葉の縁', '瑠璃の縁'], ['翡翠の水底', '月影の水面', '紅葉の小径', '瑠璃砂の庭']],
  SR: [['水晶洞の縁', '雷紋の縁', '白樹の縁', '焔銅の縁'], ['水晶の薄明', '雷雲の鏡', '白樹の影庭', '熾火の岩床']],
  SSR: [['星詠みの聖環', '龍鱗の聖縁', '深海神殿の縁', '時砂の聖縁'], ['星詠みの祭壇', '龍脈の眠り', '深海の聖床', '時砂の記憶']],
  UR: [['天穹の王座', '創生樹の根冠', '虚空の黒冠', '因果の機構'], ['観測者・月影の静域', '執行者・静寂の剣庭', '理論の化身・思索の翼', '終焉の冥灰・灰の記憶', '救済神・天穹の祈り', '草石・創生の深庭', '意志狩りの王・虚空の玉座', '時を止める神・因果の回廊']],
  EXR: [['盤理・観測者の環', '森羅・万象の縁', '因果・零点の縁', '終焉・黎明の縁'], ['盤理・無限の観測面', '森羅・世界の種床', '因果・零点の海', '終焉・夜明けの原盤', '因果抹消・忘却の星海', '究極破壊・最後の黎明']],
};
const projections: Readonly<Record<string, NonNullable<Cosmetic['projection']>>> = {
  'background-ur-01': { character: '盤理の観測者', veil: 0.30 },
  'background-ur-02': { character: '盤界の執行者', veil: 0.30 },
  'background-ur-03': { character: '理論の化身', veil: 0.32 },
  'background-ur-04': { character: '終焉の冥灰', veil: 0.30 },
  'background-ur-05': { character: '救済神', veil: 0.44 },
  'background-ur-06': { character: '草石', veil: 0.44 },
  'background-ur-07': { character: '意志狩りの王', veil: 0.34 },
  'background-ur-08': { character: '時を止める神', veil: 0.42 },
  'background-exr-01': { character: '盤理の観測者', veil: 0.32 },
  'background-exr-02': { character: '森羅万象神', veil: 0.44 },
  'background-exr-03': { character: '理論の化身', veil: 0.34 },
  'background-exr-04': { character: '終焉の冥灰', veil: 0.34 },
  'background-exr-05': { character: '因果抹消神石', veil: 0.40 },
  'background-exr-06': { character: '究極破壊神', veil: 0.40 },
};
export const surfaceVeil = (item: Cosmetic): string => `rgba(6, 16, 22, ${item.projection?.veil ?? 0})`;
export const COSMETICS: readonly Cosmetic[] = Object.freeze(RARITIES.flatMap(rarity =>
  names[rarity].flatMap((list, kindIndex) => list.map((name, index) => {
    const prefix = kindIndex === 0 ? 'frame' : 'background';
    const id = `${prefix}-${rarity.toLowerCase()}-${String(index + 1).padStart(2, '0')}`;
    return Object.freeze({ id, name, rarity, kind: kindIndex === 0 ? 'board-frame' : 'board-background',
      image: `/assets/gacha/${prefix}s/${id}.webp?v=${COSMETIC_ART_VERSION}`,
      thumbnail: `/assets/gacha/thumbs/${id}.webp?v=${COSMETIC_ART_VERSION}`,
      initial: false, ...(projections[id] ? { projection: projections[id] } : {}) } as Cosmetic);
  })),
));
export const INITIAL_COSMETICS: readonly Cosmetic[] = [
  { id: 'frame-default', name: 'はじまりの木枠', rarity: 'N', kind: 'board-frame', initial: true,
    image: '/assets/images/board/board-frame-open-top-v2.png', thumbnail: '/assets/gacha/thumbs/frame-default.webp' },
  { id: 'background-default', name: 'はじまりの盤面', rarity: 'N', kind: 'board-background', initial: true,
    image: '/assets/images/board/board-surface-bluegreen-felt-v1.png', thumbnail: '/assets/gacha/thumbs/background-default.webp' },
];
const catalog = new Map([...INITIAL_COSMETICS, ...COSMETICS].map(item => [item.id, item]));
export const getCosmetic = (id: string): Cosmetic | undefined => catalog.get(id);
export const kindLabel = (kind: CosmeticKind): string => kind === 'board-frame' ? 'フレーム' : '盤面内背景';
